import {jsx} from 'panel';

import {SelectEl} from 'common/components/select';
import {QueryBlockEl} from 'common/widgets/query-block';
import {serializeAttributes} from 'common/util';
import {getIconForPropertyType} from 'common/report/util/properties';

import type {QueryEntrySection} from '../index';
import {isUserBasedType, QueryMetricEntry, QuerySuperEntry} from '../types';

export function RowNumericAggregator({el, index}: {el: QueryEntrySection; entry: QuerySuperEntry; index: number}) {
  const sections = el.helpers.getSelectorAggregationsForEntry(index);
  if (!sections.length) {
    return null;
  }

  return (
    <div sel='.mp-query-entry-aggregator'>
      <SelectEl
        key='entry-aggregator'
        attrs={{
          elref: `aggregatorSelect`,
          'always-show-caret': true,
          'selected-option': el.helpers.getSelectorAggregationLabelForEntry(index),
          'disable-search': true,
          'menu-width': 280,
          'menu-height': 320,
          theme: `select`,
          'track-name': `operator`,
        }}
        on={{
          change: (ev: CustomEvent) =>
            el.helpers.onEntryAggregationFunctionSelected({index, type: ev.detail.item.value, isSubAggregation: false}),
          menuChange: (ev) => el.helpers.onSelectMenuChange(ev, index, el.helpers.EntryPart.Aggregator),
        }}
        props={{
          sections,
        }}
      />
    </div>
  );
}

export function RowNumericSubAggregator({el, index}: {el: QueryEntrySection; entry: QuerySuperEntry; index: number}) {
  const sections = el.helpers.getSelectorSubAggregationsForEntry(index);
  if (!sections.length) {
    return null;
  }

  return (
    <div sel='.mp-query-entry-subaggregator'>
      Aggregate by
      <SelectEl
        key='entry-subaggregator'
        attrs={{
          elref: `subAggregatorSelect`,
          'always-show-caret': true,
          'selected-option': el.helpers.getSelectorSubAggregationLabelForEntry(index),
          'menu-width': 320,
          'menu-height': 340,
          'track-name': `2-pass operator`,
          theme: `select`,
        }}
        on={{
          change: (ev: CustomEvent) =>
            el.helpers.onEntryAggregationFunctionSelected({index, type: ev.detail.item.value, isSubAggregation: true}),
          menuChange: (ev) => el.helpers.onSelectMenuChange(ev, index, el.helpers.EntryPart.SubAggregator),
        }}
        props={{
          sections: [{items: el.helpers.getSelectorSubAggregationsForEntry(index)}],
        }}
      />
    </div>
  );
}

export function RowNumericProperty({
  el,
  entry,
  index,
  isPlaceholder,
}: {
  el: QueryEntrySection;
  entry: QueryMetricEntry;
  index: number;
  isPlaceholder: boolean;
}) {
  const {activeEntry, entryPart} = el.state;
  const isUserBased = isUserBasedType(entry);
  const property = entry.property || null;

  return (
    <QueryBlockEl
      key='event-property'
      class={{
        'is-placeholder': isPlaceholder,
      }}
      attrs={serializeAttributes({
        elref: `propertyAggregationBlock`,
        type: `properties`,
        'show-special-date-property': true,
        open: isPlaceholder && activeEntry === index && entryPart === el.helpers.EntryPart.NumericProperty,
        'all-data-groups': el.attr(`data-groups-enabled`) && isUserBased,
        'data-group-id': el.attr(`data-group-id`) || entry.dataGroupId,
        'enable-workspace-filter-restrictions': el.attr(`enable-workspace-filter-restrictions`),
        'event-names': entry.event.value ? [entry.event.value] : [],
        'include-custom-properties': el.attr(`include-custom-properties`),
        'hide-events': !el.helpers.isEventBasedType(entry.type),
        'hide-edit-controls': el.attr(`hide-edit-controls`),
        'project-has-people': el.attr(`project-has-people`) && isUserBased,
        'project-id': el.attr(`project-id`),
        selected: property,
        'single-data-group': isUserBased ? entry.dataGroupId : null,
        'selector-type': `button`,
        'selector-icon': getIconForPropertyType(property?.type) || `type-number`,
        theme: `select`,
        'track-name': el.attr(`track-name`),
        'user-id': el.attr(`user-id`),
        'workspace-id': el.attr(`workspace-id`),
      })}
      on={{
        select: (ev: CustomEvent) => el.helpers.onEntryPropertySelected(index, ev.detail.item),
        remove: () => el.helpers.onEntryPropertyRemoved(index),
        open: () => el.helpers.setActiveEntry(`open`, index, el.helpers.EntryPart.NumericProperty),
        close: () => el.helpers.setActiveEntry(`close`, index, el.helpers.EntryPart.NumericProperty),
        track: el.helpers.preventTracking,
      }}
    />
  );
}
